/**
 * The claim record, and the chain that has to sign it.
 *
 * Submitter → Sponsor → HR verification → **HR Director**. The last two are different people by
 * construction (D-39): the Director hat comes from an assignment, never from holding `admin`,
 * and a database CHECK refuses a claim whose verifier and approver are the same person. Without
 * that asymmetry the chain collapses to one signature, so the screen states who did what.
 */
import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { ClaimActions } from '@/components/ClaimActions';
import { ApprovalDialog } from '@/components/ApprovalDialog';
import { ArchivePanel } from '@/components/EvidencePanel';
import { EvidenceLinks, SettledPanel, SettlementPanel } from '@/components/SettlementPanel';
import { AuditTrail } from '@/components/AuditTrail';
import { ClaimStatusBadge, ErrorNote, Money, PageHeader, RefLink } from '@/components/bits';
import { Alert, Badge, Button, Card, Facts, Table, Td, Th } from '@/components/ui';
import { activityLabel } from '@/lib/policy';
import { formatDate, formatInstant } from '@/lib/dates';

export function ClaimDetail() {
  const params = useParams();
  const id = Number(params.id);
  const session = useSession();
  const cache = useQueryClient();
  const [approving, setApproving] = useState(false);

  const claim = useQuery({ queryKey: ['claim', id], queryFn: () => api.claim(id) });
  /**
   * The preview is only meaningful *before* settlement. Afterwards it re-simulates against a
   * ledger where this claim's own usage is already written, and correctly returns S$0 — which
   * on screen reads as "this claim paid nothing". So a settled claim shows its recorded figures
   * and this query does not run at all.
   */
  const settled = claim.data?.settlement.settled_at !== null;
  const settlement = useQuery({
    queryKey: ['settlement', id],
    queryFn: () => api.settlement(id),
    enabled: claim.data !== undefined && !settled,
  });

  const decide = useMutation({
    mutationFn: (input: { exceptionId: number; decision: 'confirmed' | 'declined' }) =>
      api.decideClaimException(id, input.exceptionId, { decision: input.decision }),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ['claim', id] });
      await cache.invalidateQueries({ queryKey: ['settlement', id] });
    },
  });

  if (claim.isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (claim.error) return <ErrorNote error={claim.error} />;
  const record = claim.data;
  if (!record) return null;

  const verifying = record.status === 'hr_verify';

  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {record.ref_no ?? 'Draft claim'}
            <ClaimStatusBadge status={record.status} />
            {record.pending_exceptions > 0 ? (
              <Badge tone="warn">
                {record.pending_exceptions} pending{' '}
                {record.pending_exceptions === 1 ? 'exception' : 'exceptions'}
              </Badge>
            ) : null}
          </span>
        }
        subtitle={
          <>
            {activityLabel(record.activity_type)} on {formatDate(record.actual_date)} ·{' '}
            {record.department.name} · against{' '}
            <RefLink to={`/proposals/${record.proposal.id}`} refNo={record.proposal.ref_no} />
          </>
        }
        actions={<ClaimActions claim={record} onApprove={() => setApproving(true)} />}
      />

      {record.returned_reason ? (
        <Alert tone="warn" title="Returned for changes" className="mb-6">
          {record.returned_reason}
        </Alert>
      ) : null}
      {record.rejection_reason ? (
        <Alert tone="bad" title="Rejected" className="mb-6">
          {record.rejection_reason}
        </Alert>
      ) : null}
      {record.chain.sponsor_skipped ? (
        <Alert tone="neutral" title="Sponsor endorsement was skipped" className="mb-6">
          The submitter is a Sponsor of this department, so endorsing their own claim would be one
          signature pretending to be two (D-39).
        </Alert>
      ) : null}
      {record.status === 'hr_director' && !session.me.capabilities.hr_director ? (
        <Alert tone="neutral" title="Waiting on an HR Director" className="mb-6">
          Final approval is a Director&rsquo;s, and it must be someone other than whoever verified
          it (D-39). You are not designated as one, so this queue is here for visibility.
        </Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        <div className="grid min-w-0 gap-6">
          <Card title="The claim">
            <Facts
              items={[
                { label: 'Event date', value: formatDate(record.actual_date) },
                { label: 'Total cost', value: <Money cents={record.total_cost_cents} /> },
                {
                  label: 'Paid by',
                  value:
                    record.payment.mode === 'personal'
                      ? `reimburse employee #${record.payment.payee_employee_id}`
                      : record.payment.mode === 'corporate_card'
                        ? `corporate card — ${record.payment.cardholder_name}`
                        : record.payment.mode === 'other'
                          ? record.payment.other_description
                          : '—',
                },
                {
                  label: 'Attendance',
                  value:
                    record.attendee_count === null
                      ? '—'
                      : `${record.attendee_count} of ${record.department_headcount ?? '—'}${
                          record.participation_pct === null
                            ? ''
                            : ` · ${Math.round(record.participation_pct)}%`
                        }`,
                },
              ]}
            />
          </Card>

          <Card title="Evidence">
            <EvidenceLinks
              driveUrl={record.evidence.drive_url}
              archiveUrl={record.evidence.archive_url}
            />
            {record.evidence.shared_ack ? null : (
              <Alert tone="warn" title="The submitter has not confirmed sharing" className="mt-3">
                Both the link and the confirmation are required to submit — if this is empty on a
                submitted claim, something has gone wrong.
              </Alert>
            )}
          </Card>

          {verifying && session.isHr ? <ArchivePanel claim={record} /> : null}

          <Card title={`Who came — ${record.attendees.length}`}>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>No.</Th>
                  <Th>Department</Th>
                  <Th>On the namelist</Th>
                  <Th numeric>Settled</Th>
                </tr>
              </thead>
              <tbody>
                {record.attendees.map((attendee) => (
                  <tr key={attendee.employee_id}>
                    <Td>
                      {attendee.full_name}
                      {attendee.is_senior ? (
                        <span className="ml-2">
                          <Badge>senior</Badge>
                        </span>
                      ) : null}
                    </Td>
                    <Td className="tabular text-xs">{attendee.employee_no}</Td>
                    <Td className="text-xs">{attendee.department_name ?? '—'}</Td>
                    <Td>
                      {attendee.pre_vetted ? (
                        <Badge tone="good">pre-vetted</Badge>
                      ) : (
                        <Badge tone="warn">not pre-vetted (R-26)</Badge>
                      )}
                    </Td>
                    <Td numeric>
                      <Money cents={attendee.allocated_cents} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          {record.exceptions.length > 0 ? (
            <Card title="Exceptions">
              <ul className="grid gap-4">
                {record.exceptions.map((exception) => (
                  <li key={exception.id} className="grid gap-1.5 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        tone={
                          exception.decision === 'pending'
                            ? 'warn'
                            : exception.decision === 'confirmed'
                              ? 'good'
                              : 'bad'
                        }
                      >
                        {exception.rule_id} · {exception.decision}
                      </Badge>
                      <span className="text-muted-foreground text-xs">
                        raised {formatInstant(exception.raised_at)}
                      </span>
                    </div>
                    <p>{exception.justification}</p>
                    {exception.decision_note ? (
                      <p className="text-muted-foreground text-xs">HR: {exception.decision_note}</p>
                    ) : null}
                    {exception.decision === 'pending' && session.isHr ? (
                      <div className="mt-1 flex gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          busy={decide.isPending}
                          onClick={() =>
                            decide.mutate({ exceptionId: exception.id, decision: 'confirmed' })
                          }
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          busy={decide.isPending}
                          onClick={() =>
                            decide.mutate({ exceptionId: exception.id, decision: 'declined' })
                          }
                        >
                          Decline
                        </Button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
              {record.pending_exceptions > 0 ? (
                <Alert tone="warn" title="This claim cannot be approved yet" className="mt-4">
                  An exception is outstanding. HR decides it first — refused in the service and by a
                  database trigger.
                </Alert>
              ) : null}
              {decide.error ? <ErrorNote error={decide.error} /> : null}
            </Card>
          ) : null}

          <AuditTrail entries={record.audit} />
        </div>

        <div className="grid gap-4 lg:sticky lg:top-28">
          {record.settlement.settled_at ? (
            <SettledPanel claim={record} />
          ) : (
            <SettlementPanel
              settlement={settlement.data}
              pending={settlement.isFetching}
              error={settlement.error}
              attendeeCount={record.attendees.length}
              contributingCount={
                settlement.data
                  ? record.attendees.length - settlement.data.gate_crashers.length
                  : undefined
              }
            />
          )}

          <Card title="Who has signed">
            <Facts
              items={[
                {
                  label: 'Submitted',
                  value: record.chain.submitted_at
                    ? `user #${record.chain.submitted_by} · ${formatInstant(record.chain.submitted_at)}`
                    : 'not yet',
                },
                {
                  label: 'Sponsor',
                  value: record.chain.sponsor_skipped
                    ? 'skipped (D-39)'
                    : record.chain.sponsor_action_by
                      ? `user #${record.chain.sponsor_action_by}`
                      : 'waiting',
                },
                {
                  label: 'Verified by',
                  value: record.chain.hr_verified_at
                    ? `user #${record.chain.hr_verified_by} · ${formatInstant(record.chain.hr_verified_at)}`
                    : 'waiting',
                },
                {
                  label: 'Approved by',
                  value: record.chain.hr_approved_at
                    ? `HR Director #${record.chain.hr_approved_by} · ${formatInstant(record.chain.hr_approved_at)}`
                    : 'waiting',
                },
              ]}
            />
          </Card>

          {record.payment_record.paid_at ? (
            <Card title="Paid">
              <Facts
                items={[
                  { label: 'On', value: formatDate(record.payment_record.paid_at) },
                  { label: 'Reference', value: record.payment_record.reference ?? '—' },
                  { label: 'By', value: `user #${record.payment_record.paid_by}` },
                  { label: 'Note', value: record.payment_record.note ?? '—' },
                ]}
              />
            </Card>
          ) : record.status === 'approved' ? (
            <Alert tone="neutral" title="Approved, awaiting payment">
              HR records the reimbursement date and reference here, so &ldquo;where is my
              money?&rdquo; does not become an email (D-29).
            </Alert>
          ) : null}
        </div>
      </div>

      <ApprovalDialog
        open={approving}
        claim={record}
        settlement={settlement.data}
        onClose={() => setApproving(false)}
      />
    </>
  );
}
